





































import { Base } from '@master/model/Base'
import { FormStructure, Input, TableSetup } from '@master/model/structure/FormStructure'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import AppCheckboxGroup from '@core/components/AppCheckboxGroup.vue'
import AppFormTable from '@core/components/AppFormTableV2.vue'
import AppRadioSelection from '@core/components/AppRadioSelection.vue'
import AppSmartSelect from '@core/components/AppSmartSelect.vue'
import { inject } from 'inversify-props'
import { IDateService } from '@master/services/IDateService'
import AppDateInput from './AppDateInput.vue'
import { getMaxChar } from '@master/annotation/MaxChar'

@Component({
  components: {
    AppCheckboxGroup,
    AppRadioSelection,
    AppSmartSelect,
    AppDateInput,
    AppFormTable
  }
})
export default class AppFormV2<T extends Base> extends Vue {
  @Prop() readonly structures!: FormStructure<T>[]
  @Prop() readonly model!: T
  @Prop() readonly errors?: { key: string; msg: string }[]
  @Prop({ default: () => { return Date.now() } }) readonly uniqueID: string
  @Prop({ default: false }) readonly readOnly: boolean

  @inject()
  private dateService: IDateService

  private minimal = true
  private renderCnt = 0
  private focusId = null
  private fullErr = null

  get structuresDetails () {
    return this.structures.map((structure) => {
      const key = (structure as any).key
      const errors = this.errors?.filter(err => err.key === key) || []
      const maxChar = getMaxChar(this.model, key)
      const charLeft = this.getCharLeft(key)
      const counterMsg = maxChar ? (charLeft > 0 ? `${charLeft} letters remaining` : 'Max character limit reached') : ''
      return {
        errors,
        hasErrors: errors?.length > 0,
        maxChar,
        counterMsg,
        ...structure
      }
    })
  }

  @Watch('structuresDetails', { deep: true })
  focusError (swe) {
    const first = swe.find(s => 'msg' in s)
    if (!first) return
    const ele = document.getElementById(`box-${this.getId(first.key)}`)
    if (!ele) return
    ele.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  shouldDisabled (condition: boolean | Function = false) {
    if (this.readOnly) return true
    if (condition === undefined) {
      return false
    } else if (condition instanceof Function) {
      return condition(this.model)
    } else {
      return condition
    }
  }

  shouldShow (condition: boolean | Function = true) {
    if (condition === undefined) {
      return false
    } else if (condition instanceof Function) {
      return condition(this.model)
    } else {
      return condition
    }
  }

  getValueForTable (input: TableSetup<Base>) {
    if (input.key) {
      return this.model[input.key]
    } else if (input.data) {
      return input.data
    } else {
      throw new Error(`Invalid config for label ${input.label}: key or model is required`)
    }
  }

  getValue (input: Input<Base>) {
    if (input.key) {
      return this.model[input.key]
    } else if (input.model) {
      return input.model.get()
    } else {
      throw new Error(`Invalid config for label ${input.label}: key or model is required`)
    }
  }

  setValue (input: Input<T>, value: T[keyof T]) {
    if (input.key) {
      if (input.onChange) {
        input.onChange(this.model[input.key], value)
      }
      // when only has getter (for currency input), sidestep the error
      try {
        this.model[input.key] = value
      } catch (ex) {}
    } else if (input.model) {
      if (input.onChange) {
        input.onChange(this.model[input.key], value)
      }
      return input.model.set(value)
    } else {
      throw new Error(`Invalid config for label ${input.label}: key or model is required`)
    }
  }

  getId (key: string) {
    if (this.model && 'snakeCaseKey' in this.model) {
      return `${this.model.snakeCaseKey(key)}`
    }
    return `${key}`
  }

  getLabelId (key: string) {
    return this.getId(key) + '_label'
  }

  getCharLeft (key: string) {
    const mc = getMaxChar(this.model, key)
    return mc ? mc - this.model[key]?.length || 0 : mc
  }

  arrangeCols () {
    const curAppForm = this.$el
    const hasOverflow = curAppForm.scrollWidth > curAppForm.clientWidth
    if (hasOverflow) this.minimal = true
  }

  focusInner (structure: any) {
    // this means label is also editable, thus do not auto focus input
    if (structure.labelModel || structure.labelKey) return
    const eleId = this.getId(structure.key)
    this.focusId = eleId
    const ele = document.getElementById(eleId)
    if (ele) ele.focus()
  }

  @Watch('structures')
  structuresUpdated () {
    // this is to allow view to be updated when structures change
    this.renderCnt++
  }

  updated () {
    this.arrangeCols()
  }

  mounted () {
    this.arrangeCols()
    window.addEventListener('resize', () => this.arrangeCols())
  }
}
